/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import { useForm, FormProvider, type SubmitHandler } from 'react-hook-form'
import Grid from '@mui/material/Grid'
import { useAppDispatch } from '../../../store'
import { createAcademicUnit, editAcademicUnit, getAcademicUnitList } from '../../../store/slices/academicUnitSlice/apis'
import { academicUnitDetailsForChildren } from '../../../store/slices/academicUnitSlice/selectors'
import type { AcademicUnitRowData, AcademicUnitData } from '../types'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import FormInput from '../../../components/FormElements/FormInput'
import ValidationAlert from '../../../components/ValidationAlert'
import { bindSetError, showServerFieldErrors } from '../../../utils/showServerFieldErrors'
import DefaultModalActions from '../../../components/GenericModal/DefaultModalActions'
import { notification } from '../../../components/Notifications'
import { useAccess } from '../../../hooks/useAccess'
import { READONLY_TOOLTIP } from '../../../consts'

interface CreateNewAcademicUnitProps {
  selectedRow?: AcademicUnitRowData
  selectedRowWithActions?: any
  handleCloseModal?: () => void
}
interface FormType { children?: any[] | undefined, categoryName: string }

const academicSchema = yup.object().shape({
  categoryName: yup
    .string()
    .required('Title is required')
    .matches(/^[A-Za-z\s]+$/, 'Title must contain only alphabets'),
  children: yup.array()
})

const CreateNewAcademicUnit: React.FC<CreateNewAcademicUnitProps> = ({ handleCloseModal = () => { }, selectedRow, selectedRowWithActions }): JSX.Element => {
  const { readonly } = useAccess(selectedRowWithActions?.actions ?? [])
  const [errors, setErrors] = useState<unknown>({})
  const academicUnitData = academicUnitDetailsForChildren()
  const isEditAction = selectedRow !== undefined

  const validChildren = academicUnitData?.map((data: AcademicUnitData) => ({ categoryName: data?.data?.categoryName }))
  const lastChild = (validChildren && validChildren.length > 0) ? validChildren[validChildren.length - 1].categoryName : ''
  const getChildrensOnEdit = selectedRow?.children?.map((children) => ({ categoryName: children }))
  const methods = useForm({
    resolver: yupResolver(academicSchema),
    defaultValues: {
      categoryName: selectedRow?.categoryName,
      children: selectedRow ? getChildrensOnEdit?.map(child => child.categoryName) : undefined
    }
  })

  const dispatch = useAppDispatch()
  const { handleSubmit, formState: { isDirty, isSubmitting } } = methods
  const isEditing = selectedRow

  const onSubmit: SubmitHandler<FormType> = async ({ categoryName, children }): Promise<void> => {
    try {
      const payload = { categoryName, children }
      if (isEditAction) {
        if (!selectedRow?.categoryId) {
          notification('No categoryId provided for editing.', { variant: 'error' })
          return
        }
        await dispatch(
          editAcademicUnit({ urlId: selectedRow.categoryId, data: payload })
        ).unwrap()
      } else {
        await dispatch(createAcademicUnit({ data: payload })).unwrap()
      }

      await dispatch(getAcademicUnitList({})).unwrap()

      handleCloseModal?.()
    } catch (error) {
      setErrors(showServerFieldErrors(error, bindSetError(methods.setError), methods.clearErrors))
    }
  }

  return (
    <FormProvider {...methods}>
      <ValidationAlert errors={errors} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <FormInput name='categoryName' type='text' label='Title' size='medium' required />
          </Grid>
          <Grid item xs={12} >
            <FormInput
              name='children'
              label='Child of'
              defaultValue={lastChild}
              disabled={true}
            />
            <DefaultModalActions
              destructiveButtonProps={{
                onClick: handleCloseModal
              }}
              confirmButtonProps={{
                text: isEditing ? 'Save' : 'Create',
                type: 'submit',
                loading: isSubmitting,
                disabled: readonly || isSubmitting || (isEditing && !isDirty),
                tooltip: readonly ? READONLY_TOOLTIP : undefined
              }}
            />
          </Grid>
        </Grid>
      </form>
    </FormProvider >
  )
}

export default CreateNewAcademicUnit
